import { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MERGE_VARIABLE_OPTIONS, getLabelForVariable } from '@/lib/email-builder/merge-variables';

export interface MergeVariableModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when the user confirms the selection. */
  onSave: (variable: string, fallback?: string) => void;
  /** When set the modal opens in "edit" mode with pre-filled values. */
  initialVariable?: string;
  initialFallback?: string;
}

/**
 * Modal for inserting or editing a merge variable.
 *
 * - Dropdown with available merge variables (Contact.FirstName, etc.)
 * - Optional fallback text input
 * - Save / Cancel buttons
 * - In edit mode a "Remove" button is also shown
 */
export function MergeVariableModal({
  isOpen,
  onClose,
  onSave,
  initialVariable,
  initialFallback,
}: MergeVariableModalProps) {
  const isEditMode = initialVariable != null;

  const [selectedVariable, setSelectedVariable] = useState(
    initialVariable ?? MERGE_VARIABLE_OPTIONS[0].variable,
  );
  const [fallback, setFallback] = useState(initialFallback ?? '');

  // Reset state when the modal opens with new initial values
  const [openedWith, setOpenedWith] = useState({ isOpen, initialVariable, initialFallback });
  if (
    openedWith.isOpen !== isOpen ||
    openedWith.initialVariable !== initialVariable ||
    openedWith.initialFallback !== initialFallback
  ) {
    setOpenedWith({ isOpen, initialVariable, initialFallback });
    if (isOpen) {
      setSelectedVariable(initialVariable ?? MERGE_VARIABLE_OPTIONS[0].variable);
      setFallback(initialFallback ?? '');
    }
  }

  const handleSave = useCallback(() => {
    onSave(selectedVariable, fallback.trim() || undefined);
    onClose();
  }, [selectedVariable, fallback, onSave, onClose]);

  const title = isEditMode ? 'Edit Merge Variable' : 'Insert Merge Variable';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-5">
        {/* Variable selector */}
        <div className="space-y-2">
          <Label htmlFor="merge-var-select">Variable</Label>
          <Select
            id="merge-var-select"
            value={selectedVariable}
            onChange={(e) => setSelectedVariable(e.target.value)}
            className="h-10 w-full"
          >
            {MERGE_VARIABLE_OPTIONS.map((opt) => (
              <option key={opt.variable} value={opt.variable}>
                {opt.label}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            Resolves to <code className="rounded bg-muted px-1">{`{{${selectedVariable}}}`}</code> at
            send time.
          </p>
        </div>

        {/* Fallback */}
        <div className="space-y-2">
          <Label htmlFor="merge-var-fallback">
            Fallback <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Input
            id="merge-var-fallback"
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            placeholder={`e.g. "${getLabelForVariable(selectedVariable)}"`}
            maxLength={200}
          />
          <p className="text-xs text-muted-foreground">
            Displayed when the variable has no value for a contact.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            {isEditMode ? 'Update' : 'Insert'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
